import React, { useState, useMemo } from 'react';
import {
    createColumnHelper,
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    SortingState,
    flexRender,
} from '@tanstack/react-table';

import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from '@/shared/ui/Pagination';
import { HoverCard, HoverCardContent, HoverCardTrigger, SortableHeaderCell } from '@/shared/ui';
import { Data, Mode } from '@/pages/OrderBook';
import { formatWithCommas } from '@/shared/utils/helpers';

interface OrderBookTableProps {
    data: Data[];
    label: string;
}

const COLUMN_IDS = {
    COL1: 'col1',
    COL2: 'col2',
    COL3: 'col3',
    COL4: 'col4',
    COL5: 'col5',
} as const;

const numericSortingFn = (rowA: any, rowB: any, columnId: string) => {
    const a = parseFloat(rowA.getValue(columnId) || '0');
    const b = parseFloat(rowB.getValue(columnId) || '0');
    return a - b;
};

const alphabeticSortingFn = (rowA: any, rowB: any, columnId: string) => {
    const a = String(rowA.getValue(columnId) || '');
    const b = String(rowB.getValue(columnId) || '');
    return a.localeCompare(b);
};

export const OrderBookTable: React.FC<OrderBookTableProps> = ({ data, label }) => {
    const [sorting, setSorting] = useState<SortingState>([]);

    const columnHelper = createColumnHelper<Data>();

    const handleSort = (columnId: string) => {
        setSorting([
            { id: columnId, desc: sorting[0]?.id === columnId ? !sorting[0].desc : false },
        ]);
    };

    const columns = useMemo(() => {
        const cols = [
            columnHelper.accessor(COLUMN_IDS.COL1, {
                header: () => (
                    <SortableHeaderCell onClick={() => handleSort(COLUMN_IDS.COL1)}>
                        {label === Mode.BORROW ? Mode.BORROW : Mode.LEND}
                    </SortableHeaderCell>
                ),
                cell: (info) => info.getValue(),
                sortingFn: alphabeticSortingFn,
            }),
            columnHelper.accessor(COLUMN_IDS.COL3, {
                header: () => (
                    <SortableHeaderCell onClick={() => handleSort(COLUMN_IDS.COL3)}>
                        Amount (USD Value)
                    </SortableHeaderCell>
                ),
                cell: (info) => `$${formatWithCommas(info.getValue())}`,
                sortingFn: numericSortingFn,
            }),
            columnHelper.accessor(COLUMN_IDS.COL4, {
                header: () => (
                    <SortableHeaderCell onClick={() => handleSort(COLUMN_IDS.COL4)}>
                        APY
                    </SortableHeaderCell>
                ),
                cell: (info) => info.getValue(),
                sortingFn: numericSortingFn,
            }),
            columnHelper.accessor(COLUMN_IDS.COL5, {
                header: () => (
                    <div className='relative flex items-center gap-3 bg-fourth px-5 py-4 text-md font-semibold hover:after:absolute hover:after:inset-0 hover:after:bg-[rgba(0,0,0,0.04)] cursor-pointer select-none'>
                        {label === Mode.LEND ? 'Comment / Terms' : 'Usecase / LTV'}
                    </div>
                ),
            }),
        ];

        if (label === Mode.BORROW) {
            cols.splice(
                1,
                0,
                columnHelper.accessor(COLUMN_IDS.COL2, {
                    header: 'Collateral',
                }),
            );
        }

        return cols;
    }, [label, sorting]);

    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10,
    });

    const table = useReactTable({
        data,
        columns,
        state: { sorting, pagination },
        onSortingChange: setSorting,
        onPaginationChange: setPagination,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        pageCount: Math.ceil(data.length / pagination.pageSize),
    });

    const renderHeader = (header: any) => {
        if (header.isPlaceholder) return null;

        const headerDef = header.column.columnDef.header;
        return typeof headerDef === 'function' ? headerDef(header.getContext()) : headerDef;
    };

    return (
        <div className='w-full overflow-hidden'>
            <table className='border-collapse w-full bg-primary border border-opaque table-fixed overflow-hidden max-w-full'>
                <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <tr className='border-b border-opaque' key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                                <th
                                    key={header.id}
                                    className={`whitespace-nowrap text-left bg-background-secondary 
                                    ${header.column.id === COLUMN_IDS.COL1 ? 'w-[200px]' : ''} 
                                    ${header.column.id === COLUMN_IDS.COL2 ? 'w-[200px]' : ''} 
                                    ${header.column.id === COLUMN_IDS.COL3 ? 'w-[200px]' : ''} 
                                    ${header.column.id === COLUMN_IDS.COL4 ? 'w-[200px]' : ''} 
                                    ${header.column.id === COLUMN_IDS.COL5 ? 'w-full' : ''}`}
                                >
                                    {renderHeader(header)}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {table.getRowModel().rows.map((row) => (
                        <tr className='border-b border-opaque' key={row.id}>
                            {row.getVisibleCells().map((cell) => (
                                <td
                                    key={cell.id}
                                    className='whitespace-nowrap p-5 text-left h-[72px] font-semibold'
                                >
                                    <HoverCard>
                                        <HoverCardTrigger
                                            asChild
                                            className='hover:text-content-primary'
                                        >
                                            <span className='inline-block max-w-full whitespace-nowrap overflow-hidden text-ellipsis'>
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext(),
                                                )}
                                            </span>
                                        </HoverCardTrigger>
                                        <HoverCardContent>
                                            <p className='text-custom-small font-normal leading-custom-line tracking-custom-spacing'>
                                                {String(cell.getValue())}
                                            </p>
                                        </HoverCardContent>
                                    </HoverCard>
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>

            <div className='p-5 flex justify-between flex-row'>
                <div className='flex text-md leading-5 font-normal text-content-secondary font-sans tracking-tightest'>
                    Items per page: {table.getState().pagination.pageSize}
                </div>
                <Pagination>
                    <PaginationContent>
                        <PaginationPrevious
                            onClick={() => table.previousPage()}
                            disabled={!table.getCanPreviousPage()}
                            className='p-3 h-[42px] [&_svg_path]:fill-primary'
                        />
                        {Array.from({ length: table.getPageCount() }, (_, index) => (
                            <PaginationItem key={index}>
                                <PaginationLink
                                    isActive={table.getState().pagination.pageIndex === index}
                                    onClick={() => table.setPageIndex(index)}
                                    className={`min-w-[44px] h-[44px] font-bold p-3 text-secondary text-sm leading-none ${
                                        table.getState().pagination.pageIndex === index
                                            ? 'rounded border border-opaque bg-inversePrimary text-inversePrimary'
                                            : ''
                                    }`}
                                >
                                    {index + 1}
                                </PaginationLink>
                            </PaginationItem>
                        ))}
                        <PaginationNext
                            onClick={() => table.nextPage()}
                            disabled={!table.getCanNextPage()}
                            className='p-3 h-[42px] [&_svg_path]:fill-primary'
                        />
                    </PaginationContent>
                </Pagination>
            </div>
        </div>
    );
};
